import { Fragment } from 'react';
import {
  Box,
  Flex,
  Heading,
  Icon,
  Image,
  SimpleGrid,
  Stack,
  Tag,
  Text,
  useColorMode,
} from '@chakra-ui/react';
import { MapPin, Plane } from 'lucide-react';
import { TracingBeam } from '../ui-aceternity/tracing-beam';
import engineeringJourney, {
  JourneyEntry,
  JourneyMetric,
} from '../../data/engineeringJourney';

type EngineeringJourneyProps = {
  headingAs?: 'h1' | 'h2';
};

function MetricBand({
  metrics,
  colorMode,
}: {
  metrics: JourneyMetric[];
  colorMode: string;
}) {
  return (
    <SimpleGrid
      columns={{ base: 1, sm: metrics.length }}
      borderTop="1px solid"
      borderBottom="1px solid"
      borderColor={`mode.${colorMode}.border`}
    >
      {metrics.map((metric, index) => (
        <Box
          key={metric.label}
          py={4}
          px={{ base: 0, sm: 4 }}
          borderTopWidth={{ base: index > 0 ? '1px' : 0, sm: 0 }}
          borderLeftWidth={{ base: 0, sm: index > 0 ? '1px' : 0 }}
          borderColor={`mode.${colorMode}.border`}
        >
          <Text
            fontSize="xl"
            fontWeight="bold"
            color={`mode.${colorMode}.text`}
          >
            {metric.value}
          </Text>
          <Text mt={1} fontSize="xs" color={`mode.${colorMode}.subtext`}>
            {metric.label}
          </Text>
        </Box>
      ))}
    </SimpleGrid>
  );
}

function JourneyCard({
  entry,
  colorMode,
  headingAs,
}: {
  entry: JourneyEntry;
  colorMode: string;
  headingAs: 'h2' | 'h3';
}) {
  return (
    <Box
      as="article"
      bg={`mode.${colorMode}.cardBG`}
      border="1px solid"
      borderColor={`mode.${colorMode}.border`}
      rounded="md"
      overflow="hidden"
    >
      <Stack spacing={6} p={{ base: 5, md: 8 }}>
        <Flex
          direction={{ base: 'column', sm: 'row' }}
          align={{ base: 'flex-start', sm: 'center' }}
          justify="space-between"
          gap={4}
        >
          <Flex align="center" gap={4} minW={0}>
            <Flex
              align="center"
              justify="center"
              w={14}
              h={14}
              flexShrink={0}
              bg="white"
              border="1px solid"
              borderColor={`mode.${colorMode}.border`}
              rounded="md"
              p={2}
            >
              <Image
                src={entry.image}
                alt={entry.imageAlt}
                w="100%"
                h="100%"
                objectFit="contain"
              />
            </Flex>

            <Box minW={0}>
              <Flex align="center" gap={3} wrap="wrap">
                <Heading
                  as={headingAs}
                  fontSize={{ base: 'xl', md: '2xl' }}
                  color={`mode.${colorMode}.text`}
                >
                  {entry.company}
                </Heading>
                <Tag
                  size="sm"
                  px={2}
                  py={0.5}
                  rounded="sm"
                  whiteSpace="nowrap"
                  fontSize="10px"
                  lineHeight="short"
                  fontWeight="medium"
                  bg={`mode.${colorMode}.career.tagBG`}
                  color={`mode.${colorMode}.career.subtext`}
                >
                  {entry.category}
                </Tag>
              </Flex>
              <Text mt={1} fontSize="sm" color={`mode.${colorMode}.subtext`}>
                {entry.role}
              </Text>
            </Box>
          </Flex>

          <Tag
            flexShrink={0}
            bg={`mode.${colorMode}.accentBG`}
            border="1px solid"
            borderColor={`mode.${colorMode}.accentBorder`}
            color={`mode.${colorMode}.accent`}
            fontWeight="bold"
            rounded="sm"
          >
            {entry.period}
          </Tag>
        </Flex>

        <Stack spacing={2}>
          <Text
            fontSize={{ base: 'md', md: 'lg' }}
            fontWeight="bold"
            color={`mode.${colorMode}.text`}
          >
            {entry.storyTitle}
          </Text>
          <Text
            maxW="68ch"
            fontSize={{ base: 'sm', md: 'md' }}
            lineHeight="tall"
            color={`mode.${colorMode}.subtext`}
          >
            {entry.story}
          </Text>
        </Stack>

        {entry.metrics && (
          <MetricBand metrics={entry.metrics} colorMode={colorMode} />
        )}
      </Stack>
    </Box>
  );
}

function RelocationMarker({ colorMode }: { colorMode: string }) {
  return (
    <Box
      as="aside"
      borderTop="1px solid"
      borderBottom="1px solid"
      borderColor={`mode.${colorMode}.border`}
      py={6}
    >
      <Flex align="center" gap={{ base: 2, sm: 4 }}>
        <Flex align="center" gap={2} flexShrink={0}>
          <Icon as={MapPin} boxSize={4} color={`mode.${colorMode}.accent`} />
          <Text fontWeight="bold" color={`mode.${colorMode}.text`}>
            Sydney
          </Text>
        </Flex>

        <Box
          position="relative"
          flex="1"
          minW={8}
          borderTop="1px dashed"
          borderColor={`mode.${colorMode}.border`}
        >
          <Flex
            position="absolute"
            left="50%"
            top="50%"
            transform="translate(-50%, -50%)"
            align="center"
            justify="center"
            w={9}
            h={9}
            bg={`mode.${colorMode}.background`}
            rounded="full"
          >
            <Icon
              as={Plane}
              boxSize={5}
              color={`mode.${colorMode}.accent`}
              transform="rotate(-12deg)"
            />
          </Flex>
        </Box>

        <Flex align="center" gap={2} flexShrink={0}>
          <Text fontWeight="bold" color={`mode.${colorMode}.text`}>
            London
          </Text>
          <Icon as={MapPin} boxSize={4} color={`mode.${colorMode}.accent`} />
        </Flex>
      </Flex>
    </Box>
  );
}

export function EngineeringJourney({
  headingAs = 'h2',
}: EngineeringJourneyProps) {
  const { colorMode } = useColorMode();
  const cardHeadingAs = headingAs === 'h1' ? 'h2' : 'h3';

  return (
    <Stack
      as="section"
      w="100%"
      spacing={{ base: 10, md: 12 }}
      aria-labelledby="engineering-journey-heading"
    >
      <Stack spacing={4} maxW="760px">
        <Tag
          alignSelf="flex-start"
          bg={`mode.${colorMode}.accentBG`}
          color={`mode.${colorMode}.accent`}
          fontWeight="bold"
          fontSize="xs"
          letterSpacing="wide"
          px={3}
          py={1}
          rounded="sm"
        >
          Journey
        </Tag>
        <Heading
          id="engineering-journey-heading"
          as={headingAs}
          color={`mode.${colorMode}.text`}
          fontSize={{ base: '3xl', sm: '4xl', md: '5xl' }}
          lineHeight="1.15"
        >
          From product foundations to financial infrastructure.
        </Heading>
      </Stack>

      <TracingBeam>
        <Stack spacing={{ base: 8, md: 12 }}>
          {engineeringJourney.map((entry) => (
            <Fragment key={entry.id}>
              {entry.id === 'vechain' && (
                <RelocationMarker colorMode={colorMode} />
              )}
              <JourneyCard
                entry={entry}
                colorMode={colorMode}
                headingAs={cardHeadingAs}
              />
            </Fragment>
          ))}
        </Stack>
      </TracingBeam>
    </Stack>
  );
}
